import { ThemeConfig } from "antd";
import { colors } from "./color";
import { textPx } from "shared/constants/zoom";

// Resting border for text/number/date/select inputs — darker than the card and
// table hairline (colors.line) so form fields read clearly. Hover stays at
// colors.lineStrong and focus at ink, both stronger than this so the focus ring
// still stands out. Form controls only — not cards/tables.
const formControlBorder = "rgba(15, 15, 14, 0.18)";

// Spott design system — AntD ConfigProvider tokens + component overrides.
// Source of truth: 2026-05-26-terasku-design-system.md ("AntD ConfigProvider
// (admin side)"), treated as philosophy not law. Deliberate divergence from the
// doc: the PRIMARY ACTION COLOR is navy (#2E3F8B), unified across admin + client.
// token.colorPrimary stays INK so text/links/focus rings/Switch read ink; navy
// is applied to primary BUTTONS via components.Button.colorPrimary below. The
// whole admin inherits this via <ConfigProvider theme={theme}> in src/pages/App.tsx.
// Colours come from the shared `colors` tokens (./color) so the palette has a
// single source of truth; only values without a semantic token stay literal.
export const theme: ThemeConfig = {
  token: {
    // Ink as primary (NOT navy)
    colorPrimary: colors.ink,
    colorSuccess: colors.success,
    colorWarning: colors.amber,
    colorError: colors.error,
    colorInfo: colors.navy,

    // Warm surfaces
    colorBgBase: colors.page,
    colorBgContainer: colors.card,
    colorBgElevated: colors.card,
    colorBgLayout: colors.pageOuter,

    // Translucent borders
    colorBorder: colors.line,
    colorBorderSecondary: colors.line,

    // Warm ink text
    colorText: colors.ink,
    colorTextSecondary: colors.ink2,
    colorTextTertiary: colors.ink3,
    colorTextDescription: colors.ink2,
    colorTextQuaternary: "#BFBFBA", // faint quaternary text — no semantic token

    // Four-tier radius (skip rounded-lg)
    borderRadius: 12, // inputs, sub-cards
    borderRadiusLG: 16, // cards
    borderRadiusSM: 6, // small chrome
    borderRadiusXS: 6,

    // Typography
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    // The design's sizes, shown at TEXT_ZOOM (see shared/constants/zoom).
    // Headings keep AntD's defaults (38 / 30 / 24 / 20 / 16).
    fontSize: textPx(14),
    fontSizeLG: textPx(16),
    fontSizeSM: textPx(12),
    fontSizeXL: textPx(20),
    fontSizeHeading1: textPx(38),
    fontSizeHeading2: textPx(30),
    fontSizeHeading3: textPx(24),
    fontSizeHeading4: textPx(20),
    fontSizeHeading5: textPx(16),
    fontWeightStrong: 600,
    lineHeight: 1.5,

    // No shadow at rest; subtle on hover
    boxShadow: "none",
    boxShadowSecondary: "0 12px 32px -8px rgba(15, 15, 14, 0.18)",

    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,

    // Selection tints. AntD derives "active/selected" tints by lightening
    // colorPrimary, but it can't lighten near-black ink — so pin them to the
    // warm bg-soft surface for readable ink-on-light selected states (sidebar
    // nav, Select options, etc.) instead of black-on-charcoal.
    colorPrimaryBg: colors.bgSoft,
    colorPrimaryBgHover: colors.bgSoftHover,
    controlItemBgActive: colors.bgSoft,
    controlItemBgActiveHover: colors.bgSoftHover,
    controlOutline: "rgba(15, 15, 14, 0.08)", // faint focus ring — no token
  },
  components: {
    Card: { borderRadiusLG: 16, paddingLG: 24, headerHeight: 56 },
    // Primary buttons render as pill-shaped NAVY on the admin (unified primary
    // action color with the client). Component-level colorPrimary overrides the
    // ink token for buttons only — links/focus/Switch keep the ink token. This
    // generalizes the former per-spot navyButtonTheme wraps into one place.
    Button: {
      borderRadius: 9999, // full pill
      controlHeight: 40,
      primaryShadow: "none",
      defaultShadow: "none",
      fontWeight: 600,
      defaultBg: colors.card,
      defaultBorderColor: colors.line,
      colorPrimary: colors.navy,
      colorPrimaryHover: colors.navyDark,
      colorPrimaryActive: colors.navyDark,
    },
    Input: {
      borderRadius: 12,
      controlHeight: 40,
      paddingInline: 14,
      colorBorder: formControlBorder,
    },
    InputNumber: {
      borderRadius: 12,
      controlHeight: 40,
      colorBorder: formControlBorder,
    },
    // Selected/active states pinned to bg-soft so ink stays readable (see the
    // colorPrimaryBg / controlItemBgActive note in the token block).
    Menu: {
      itemSelectedBg: colors.bgSoft,
      itemSelectedColor: colors.ink,
      itemActiveBg: colors.bgSoft,
      itemHoverBg: colors.bgSoft,
      itemColor: colors.ink2,
      itemHoverColor: colors.ink,
    },
    Segmented: {
      trackBg: colors.bgSoft,
      itemSelectedBg: colors.card,
      itemSelectedColor: colors.ink,
      itemColor: colors.ink2,
    },
    Select: {
      borderRadius: 12,
      controlHeight: 40,
      colorBorder: formControlBorder,
      optionSelectedBg: colors.bgSoft,
      optionSelectedColor: colors.ink,
    },
    DatePicker: {
      borderRadius: 12,
      controlHeight: 40,
      colorBorder: formControlBorder,
    },
    Modal: { borderRadiusLG: 16 },
    // Typography type="warning" (notes under a form step, e.g. Tambah Klien's
    // "reactivates them") in the TEXT-tier amber: the default derives from
    // the fill-tier amber and was hard to read on white (Andrew, 2026-09-25).
    Typography: { colorWarningText: colors.amberDark },
    Tag: {
      borderRadiusSM: 6,
      defaultBg: colors.bgSoft,
      defaultColor: colors.ink2,
    },
    Tabs: {
      itemColor: colors.ink2,
      itemActiveColor: colors.ink,
      itemSelectedColor: colors.ink,
      inkBarColor: colors.ink,
      titleFontSize: textPx(14),
    },
    Table: {
      borderRadius: 16,
      headerBg: "transparent",
      headerColor: colors.ink2,
      headerSplitColor: colors.line,
      borderColor: colors.line,
      cellPaddingBlock: 14,
    },
    Switch: { colorPrimary: colors.ink },
    Tooltip: { colorBgSpotlight: colors.ink },
    Message: { contentBg: colors.ink, colorText: colors.white },
  },
};
